/**
 * Legacy placeholder that older checkout links still carry in the
 * `vehicle_name` query parameter. It is a UI string, never a real name, so it
 * must never reach the reservation summary.
 */
const LEGACY_PLACEHOLDER = "Unknown Vehicle";

function usableName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === LEGACY_PLACEHOLDER) return null;
  return trimmed;
}

/**
 * Resolve the vehicle name to display during checkout.
 *
 * The loaded vehicle is the source of truth; the URL parameter is only a hint
 * that keeps the summary populated while the vehicle request is in flight.
 *
 * Returns `null` when no name is known yet, so callers can render a loading
 * placeholder instead of inventing one.
 */
export function resolveVehicleName(
  vehicle:
    | { name?: string | null; headline?: string | null }
    | null
    | undefined,
  fallbackName: string | null | undefined,
): string | null {
  return (
    usableName(vehicle?.headline) ??
    usableName(vehicle?.name) ??
    usableName(fallbackName)
  );
}

export type VehicleRequest = {
  /** `false` while the vehicle query is still in flight. */
  loaded: boolean;
  vehicle: { name?: string | null } | null | undefined;
};

/**
 * Describe why the checkout has no vehicle name to show, or `null` when there
 * is nothing to report.
 *
 * Stays silent until the vehicle request settles: reporting earlier would fire
 * on every checkout, since the query starts out with no data.
 */
export function vehicleNameDiagnostic(
  request: VehicleRequest,
  fallbackName: string | null | undefined,
  vehicleId?: string,
): string | null {
  if (!request.loaded) return null;
  if (resolveVehicleName(request.vehicle, fallbackName)) return null;

  const subject = vehicleId ? `vehicle "${vehicleId}"` : "the booked vehicle";

  return (
    `[FleetSync] Checkout could not resolve a vehicle name for ${subject}. ` +
    `The reservation summary will render without one. Check that the vehicle ` +
    `has a name, or pass a "vehicle-name" attribute to <fm-checkout>.`
  );
}
